import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { logAction } from "@/services/eventLogService";
import { todayInIsrael } from "@/lib/dateUtils";

interface ComebackCardProps {
  daysInactive: number;
  onDismiss: () => void;
  lessonPath: string;
}

/**
 * Greets a member who opens Home after a week or more away. No count of what
 * she missed, no catching up: one way forward, to today's lesson.
 */
export default function ComebackCard({ daysInactive, onDismiss, lessonPath }: ComebackCardProps) {
  const navigate = useNavigate();
  const { effectiveUserId, isImpersonating } = useImpersonation();

  // Once per Israel day per user (a local mark, like FirstWeeksCard's
  // `shownDay`); once per mount when storage is unavailable. An impersonating
  // admin leaves no trace.
  const shownLogged = useRef(false);
  useEffect(() => {
    if (shownLogged.current || isImpersonating || !effectiveUserId) return;
    shownLogged.current = true;
    const today = todayInIsrael();
    const key = `sy_comeback_shown:${effectiveUserId}`;
    try {
      if (localStorage.getItem(key) === today) return;
      localStorage.setItem(key, today);
    } catch {
      // Storage unavailable: fall through and log for this mount.
    }
    logAction("feature", "comeback_card_shown", { days_inactive: daysInactive });
  }, [isImpersonating, effectiveUserId, daysInactive]);

  const handleCta = () => {
    if (!isImpersonating) {
      logAction("feature", "comeback_card_cta_tapped", { days_inactive: daysInactive });
    }
    onDismiss();
    navigate(lessonPath);
  };

  return (
    <section className="my-4 rounded-2xl border border-border bg-card px-5 py-4" dir="rtl">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground">טוב שחזרת</h2>
          <p className="mt-1 text-sm text-muted-foreground">ממשיכים משיעור היום. אין צורך להשלים את מה שהיה.</p>
        </div>
        <button
          type="button"
          aria-label="סגירה"
          onClick={onDismiss}
          className="-me-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-muted"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <Button type="button" className="mt-3 h-11 w-full text-base" onClick={handleCta}>
        לשיעור של היום
      </Button>
    </section>
  );
}
